/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { AxisPosition, DataAxisProps } from '../../utils/UNSAFE_visTypes/chart';
import { Dimension } from '../../utils/UNSAFE_visTypes/common';
import { DataAxisInfo } from '../dataAxisInfo';
import { AXIS_DEFAULTS } from '../utils/axisDefaults';
import { getAxisMaxSize, getAxisRange, getTickLabelAnchor } from '../utils/axisUtils';
import { getDataAxisLabelsInfo, getTickLabelInfo, updateLabelsDims } from '../utils/labelUtils';
import { getTitleProps } from '../utils/titleUtils';
import type { GetTextDimensionsType } from '../../hooks/PRIVATE_useTextDimensions';
import { getLinearAxisValueFormatter } from '../../utils/PRIVATE_chartUtils/numberFomatUtils';
import { ScaleLinear, ScaleLog } from '../../utils/PRIVATE_chartUtils';

/**
 * The function that returns the y axis properties.
 * @param dataAxisInfo The DataAxisInfo.
 * @param dataAxis The dataAxis props.
 * @param groups The chart groups.
 * @param position The position of axis in the given space.
 * @param availSpace The available space.
 * @param isRtl Whether the reading direction is right to left.
 * @param getTextDimensions Function for text measurement.
 * @returns
 */
export function getDataAxisProps(
  position: AxisPosition,
  availSpace: Dimension,
  isRtl: boolean,
  locale: string,
  dataAxis?: DataAxisProps,
  dataAxisInfo?: DataAxisInfo,
  axisFontSize?: string,
  getTextDimensions?: GetTextDimensionsType
) {
  const [start, end] = getAxisRange(availSpace, position, isRtl);
  const axisInfo = dataAxisInfo || new DataAxisInfo({ ...dataAxis });
  const scale = axisInfo.getScale([start, end]);

  const isHoriz = position === 'top' || position === 'bottom';
  const axisMaxDim = getAxisMaxSize(availSpace, isHoriz, dataAxis?.size);
  const titleStyle = { fontSize: axisFontSize, ...dataAxis?.titleStyle };
  const { titleDim, ...titleProps } = getTitleProps(
    dataAxis?.title,
    titleStyle,
    axisMaxDim,
    availSpace,
    position,
    isRtl,
    getTextDimensions
  );
  const { width: titleWidth, height: titleHeight } = titleProps.dims!;
  const isTitleRendered = titleWidth > 0 && titleHeight > 0;
  const axisDim = isTitleRendered ? titleDim + AXIS_DEFAULTS.titleGap : 0;
  const isRotated = position === 'left' || position === 'right';

  if (isTitleRendered) {
    if (isRotated) {
      availSpace.width -= axisDim;
      availSpace.x += position === 'left' ? axisDim : 0;
    } else {
      availSpace.height -= axisDim;
      availSpace.y += position === 'top' ? axisDim : 0;
    }
  }

  const tickLabelStyle = {
    fontSize: axisFontSize,
    fill: dataAxis?.tickLabel?.style?.color,
    ...dataAxis?.tickLabel?.style,
    textAnchor: getTickLabelAnchor(position, isRtl),
    dominantBaseline: 'middle'
  };
  const ticks = axisInfo.getTicks();

  const linearFormatter = getLinearAxisValueFormatter(
    ticks[0],
    ticks[ticks.length - 1],
    axisInfo.getStep(),
    locale
  );

  let isLabelRendered = dataAxis?.tickLabel?.isRendered != false;

  const labels = isLabelRendered
    ? ticks.map((i) => {
        if (dataAxis?.tickLabel?.format) {
          return dataAxis.tickLabel.format(i);
        }

        if (dataAxis?.scale === 'log') {
          const formatter = getLinearAxisValueFormatter(i, i, i, locale);
          return formatter(i);
        }
        return linearFormatter(i);
      })
    : [];

  const maxLabelHeight = axisMaxDim - axisDim - AXIS_DEFAULTS.labelGap;

  const getLabelInfo = (tick: string | undefined, index: number, _: boolean) => {
    const coord = scale.transform(ticks[index]);
    return getTickLabelInfo(
      coord,
      tick,
      tickLabelStyle,
      isHoriz,
      false,
      false,
      false,
      isHoriz ? maxLabelHeight : axisMaxDim - axisDim,
      isHoriz ? axisMaxDim - axisDim : maxLabelHeight,
      availSpace,
      getTextDimensions
    );
  };

  const { maxWidth, maxHeight, tickLabels } = getDataAxisLabelsInfo(labels, getLabelInfo, isHoriz);

  isLabelRendered =
    isLabelRendered && isHoriz
      ? maxHeight + AXIS_DEFAULTS.labelGap < axisMaxDim - axisDim
      : maxWidth + AXIS_DEFAULTS.labelGap < axisMaxDim - axisDim;

  const getAxisPos = (index: number) => {
    return scale.transform(ticks[index]);
  };

  let labelDims = 0;
  if (isLabelRendered) {
    updateLabelsDims(maxWidth, maxHeight, tickLabels, getAxisPos, availSpace, position);
    labelDims = isHoriz ? maxHeight + AXIS_DEFAULTS.labelGap : maxWidth + AXIS_DEFAULTS.labelGap;
  }

  return {
    title: isTitleRendered ? dataAxis?.title : undefined,
    titleStyle,
    titleProps: isTitleRendered ? titleProps : undefined,
    axisDim: isTitleRendered ? labelDims + axisDim : labelDims,
    tickLabels,
    tickLabelStyle,
    scale
  };
}

export function getMaxAvailHeight(ticks: number[], scale: ScaleLinear | ScaleLog) {
  const range = scale.range();
  return Math.abs(range[1] - range[0]) / ticks.length;
}
